<template>
  <div>
    <navbar-dashboard name="Detalles de Envío" />
    <div class="ew-view">
      <router-link to="/shipments" class="ew-back"><i class="bi bi-arrow-left mr-1"></i> Gestión de envíos</router-link>

      <template v-if="shipment">
        <page-header :title="normalizedGuideNumbers.length ? 'Envío ' + normalizedGuideNumbers[0] : 'Detalle del envío'"
          :subtitle="'Creado el ' + formatDate(shipment.created_at)">
          <template v-if="normalizedGuides.length">
            <a v-for="(guidePath, index) in normalizedGuides" :key="index" :href="formatGuidePath(guidePath)"
              target="_blank" rel="noopener" class="btn btn-primary">
              <i class="bi bi-file-earmark-pdf mr-1"></i> Descargar guía{{ normalizedGuides.length > 1 ? ' ' + (index + 1) : '' }}
            </a>
          </template>
          <button v-else class="btn btn-outline-secondary" disabled>
            <i class="bi bi-file-earmark-pdf mr-1"></i> Guía no disponible
          </button>
        </page-header>

        <div class="ew-detail-grid">
          <!-- Columna principal -->
          <div>
            <div class="ew-section">
              <div class="ew-route">
                <div v-for="side in parties" :key="side.key" class="ew-route-party">
                  <div class="ew-route-label">
                    <i class="bi" :class="side.key === 'origin' ? 'bi-circle' : 'bi-geo-alt-fill'"></i> {{ side.label }}
                  </div>
                  <div class="ew-route-name">{{ side.dir.full_name || side.dir.companyName | dash }}</div>
                  <div v-if="side.dir.companyName && side.dir.full_name" class="ew-route-company">{{ side.dir.companyName }}</div>
                  <div v-if="side.dir.address" class="ew-route-line">{{ side.dir.address }}</div>
                  <div class="ew-route-line">
                    {{ [side.dir.colony, side.dir.city, side.dir.state_short || side.dir.state].filter(Boolean).join(', ') }}
                    <template v-if="side.dir.postal_code"> · C.P. {{ side.dir.postal_code }}</template>
                  </div>
                  <div class="ew-route-contact">
                    <span v-if="side.dir.phone"><i class="bi bi-telephone mr-1"></i>{{ side.dir.phone }}</span>
                    <span v-if="side.dir.email"><i class="bi bi-envelope mr-1"></i>{{ side.dir.email }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="ew-section">
              <h2 class="ew-section-title"><i class="bi bi-box-seam text-primary"></i> Paquetes</h2>
              <p class="ew-section-subtitle">{{ shipment.description_of_the_content || 'Sin descripción del contenido' }}</p>
              <p v-if="!shipment.parcels || !shipment.parcels.length" class="text-muted small mb-0">
                No hay paquetes registrados en este envío.
              </p>
              <div v-else class="table-responsive">
                <table class="table mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Dimensiones</th>
                      <th class="ew-num">Peso</th>
                      <th class="ew-num">Peso volumétrico</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(p, i) in shipment.parcels" :key="i">
                      <td>{{ i + 1 }}</td>
                      <td>{{ p.height }} × {{ p.width }} × {{ p.depth }} cm</td>
                      <td class="ew-num">{{ p.weight }} kg</td>
                      <td class="ew-num">{{ volumetricWeight(p.height, p.width, p.depth) }} kg</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="shipment.insurance_information && shipment.insurance_information.package_value" class="ew-detail-note">
                <i class="bi bi-shield-check mr-1"></i> Valor declarado: <strong>{{ shipment.insurance_information.package_value | money }}</strong>
              </div>
            </div>

            <div class="ew-section">
              <h2 class="ew-section-title"><i class="bi bi-signpost-split text-primary"></i> Rastreo</h2>
              <empty-state v-if="!shipment.tracking || shipment.tracking.length === 0" compact icon="bi bi-clock-history"
                title="Aún sin movimientos" text="El historial aparecerá en cuanto la paquetería registre el primer evento."></empty-state>
              <div v-else v-for="guideNum in uniqueGuides" :key="guideNum" class="ew-track">
                <div class="ew-track-guide">Guía {{ guideNum }}</div>
                <ol class="ew-timeline">
                  <li v-for="t in trackingFor(guideNum)" :key="t._id">
                    <div class="d-flex justify-content-between flex-wrap" style="gap: 8px;">
                      <tracking-status :status="t.status || t.estado"></tracking-status>
                      <small class="text-muted">{{ formatDate(t.updated_at) }}</small>
                    </div>
                    <p v-if="trackingDescription(t.description, t.status || t.estado)" class="mb-0 mt-1 small text-muted">
                      {{ trackingDescription(t.description, t.status || t.estado) }}</p>
                  </li>
                </ol>
              </div>
            </div>
          </div>

          <!-- Columna lateral -->
          <div>
            <div class="ew-section">
              <div class="ew-service">
                <img v-if="shipment.selected_service.carrier_logo_url_svg" :src="shipment.selected_service.carrier_logo_url_svg" alt="" height="32">
                <div>
                  <div class="ew-service-name">{{ shipment.selected_service.carrier | carrierName }}</div>
                  <div class="text-muted small">{{ shipment.selected_service.carrier_service_name | serviceName }}</div>
                </div>
              </div>
              <span class="badge mt-3" :class="shipment.guide ? 'badge-success' : 'badge-warning'">
                {{ shipment.guide ? 'Guía generada' : 'Guía pendiente' }}
              </span>
            </div>

            <div class="ew-section" v-if="!hasRole(['user'])">
              <h2 class="ew-section-title"><i class="bi bi-receipt text-primary"></i> Pago</h2>
              <dl class="ew-pay-lines">
                <template v-for="service in shipment.additionalServices">
                  <dt :key="'n' + service.id">{{ service.service_name }}</dt>
                  <dd :key="'v' + service.id">{{ service.price | money }}</dd>
                </template>
                <dt>Total por guía</dt>
                <dd>{{ shipment.selected_service.total_amount | money(shipment.selected_service.currency || 'MXN') }}</dd>
                <dt>Guías</dt>
                <dd>{{ normalizedGuideNumbers.length || shipment.parcels.length }}</dd>
                <dt class="ew-pay-total-label">Total</dt>
                <dd class="ew-pay-total-value">{{ shipmentTotal | money(shipment.selected_service.currency || 'MXN') }}</dd>
              </dl>
              <span v-if="shipment.status" class="badge badge-success"><i class="bi bi-check2 mr-1"></i>Pagado</span>
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="loadError">
        <div class="ew-section p-0">
          <empty-state error icon="bi bi-box-seam" title="No pudimos cargar este envío"
            text="Puede que ya no exista o que haya un problema de conexión.">
            <router-link to="/shipments" class="btn btn-outline-secondary">Volver a envíos</router-link>
          </empty-state>
        </div>
      </template>

      <template v-else>
        <span class="ew-skeleton mb-4" style="width: 260px; height: 1.6rem;"></span>
        <div class="ew-detail-grid">
          <div>
            <div class="ew-section"><span class="ew-skeleton mb-2"></span><span class="ew-skeleton" style="width: 70%;"></span></div>
            <div class="ew-section"><span class="ew-skeleton mb-2"></span><span class="ew-skeleton" style="width: 50%;"></span></div>
          </div>
          <div><div class="ew-section"><span class="ew-skeleton mb-2"></span><span class="ew-skeleton" style="width: 60%;"></span></div></div>
        </div>
      </template>
    </div>
  </div>
</template>


<script>
import axios from 'axios'
import Navbar from './Header/Navbar'
import { mapGetters } from 'vuex'
import commons from '../commons'
import { roleMixin } from '@/mixins/roleMixin';
import { trackingDescription } from '@/utils/format';

export default {
  name: 'Dashboard',
  mixins: [roleMixin],
  data() {
    return {
      shipmentId: '',
      shipment: null,
      loadError: false
    }
  },
  created() {
    this.shipmentId = this.$route.params.id

    this.getShipment(this.shipmentId)
      .then(response => {
        this.shipment = response.data
      })
      .catch(error => {
        console.error(error)
        this.loadError = true
      })
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    formatGuidePath(path) {
      if (!path) return null;

      // 1. Origen actual (ej: https://app.dominio.com)
      const currentOrigin = window.location.origin;

      // 2. Ruta de la API
      const apiPath = '/api/public/';

      /**
       * 3. Regex para limpiar el prefijo:
       * ^(\.\/)? -> Busca opcionalmente un punto y una diagonal al inicio
       * src\/public\/ -> Busca la cadena exacta src/public/
       */
      const prefixRegex = /^(\.\/)?src\/public\//;

      if (prefixRegex.test(path)) {
        // 4. Reemplazamos el prefijo detectado por un string vacío para obtener solo el nombre del archivo
        const filename = path.replace(prefixRegex, '');

        // 5. Retornamos la URL construida
        const finalPath = currentOrigin + apiPath + filename;
        console.log("Formatted path:", finalPath);
        return finalPath;
      }

      // Si ya es una URL completa (http...) no hacemos nada
      return path;
    },
    getShipment(id) {
      const url = `${commons.apiURL}/api/v1/shipments/${id}`
      return axios.get(url)
    },
    formatCurrency(value) {
      return Number(value).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    },
    volumetricWeight(height, width, depth) {
      return Math.round((height * width * depth / 5000) * 100) / 100
    },
    trackingDescription,
    trackingFor(guideNum) {
      return (this.shipment.tracking || []).filter(item => item.number_guide === guideNum)
    }
  },
  computed: {
    ...mapGetters(['shipments']),
    shipmentWithGuide() {
      return []
    },
    uniqueGuides() {
      if (!this.shipment.tracking) return [];
      const guides = this.shipment.tracking.map(t => t.number_guide);
      return [...new Set(guides)]; // Elimina duplicados
    },
    shipmentWithoutGuide() {
      return []
    },
    parties() {
      return [
        { key: 'origin', label: 'Origen', dir: this.shipment.origin_direction || {} },
        { key: 'destination', label: 'Destino', dir: this.shipment.destination_direction || {} },
      ]
    },
    normalizedGuideNumbers() {
      const n = this.shipment && this.shipment.number_guide;
      return n ? String(n).split(',').map(s => s.trim()).filter(Boolean) : [];
    },
    shipmentTotal() {
      const s = this.shipment;
      return s.total_guia || (Number(s.selected_service.total_amount) || 0) * (this.normalizedGuideNumbers.length || s.parcels.length || 1);
    },
    normalizedGuides() {
      if (!this.shipment.guide) return [];
      // Si ya es array, lo retornamos, si es string, lo separamos por comas
      return Array.isArray(this.shipment.guide)
        ? this.shipment.guide
        : this.shipment.guide.split(',').map(s => s.trim());
    }
  },
  components: {
    'navbar-dashboard': Navbar
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.ew-back {
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  color: #6b7185;
  font-size: 0.85rem;
  font-weight: 500;

  &:hover {
    color: #031d7d;
    text-decoration: none;
  }
}

.ew-detail-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 16px;
  align-items: start;

  @media (max-width: 991.98px) {
    grid-template-columns: 1fr;
  }
}

.ew-route {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;

  @media (max-width: 575.98px) {
    grid-template-columns: 1fr;
  }
}

.ew-route-label {
  margin-bottom: 6px;
  color: #6b7185;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;

  i {
    color: #031d7d;
  }
}

.ew-route-name {
  color: #1c2030;
  font-weight: 700;
}

.ew-route-company,
.ew-route-line {
  color: #4a5064;
  font-size: 0.875rem;
}

.ew-route-contact {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 8px;
  color: #6b7185;
  font-size: 0.8rem;
  overflow-wrap: anywhere;
}

.ew-detail-note {
  margin-top: 12px;
  color: #4a5064;
  font-size: 0.85rem;
}

.ew-track + .ew-track {
  margin-top: 20px;
}

.ew-track-guide {
  margin-bottom: 10px;
  color: #1c2030;
  font-size: 0.85rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.ew-timeline {
  margin: 0;
  padding: 0 0 0 18px;
  list-style: none;
  border-left: 2px solid #eceef5;

  li {
    position: relative;
    padding: 0 0 14px 4px;
    font-size: 0.875rem;

    &::before {
      content: "";
      position: absolute;
      top: 5px;
      left: -24px;
      width: 10px;
      height: 10px;
      border: 2px solid #fff;
      border-radius: 50%;
      background: #c5c9d6;
      box-shadow: 0 0 0 1px #c5c9d6;
    }

    &:last-child {
      padding-bottom: 0;
    }

    &:last-child::before {
      background: #031d7d;
      box-shadow: 0 0 0 1px #031d7d;
    }
  }
}

.ew-service {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ew-service-name {
  color: #1c2030;
  font-weight: 700;
}

.ew-pay-lines {
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content;
  gap: 8px 12px;
  margin: 12px 0;
  font-size: 0.875rem;

  dt {
    color: #6b7185;
    font-weight: 400;
  }

  dd {
    margin: 0;
    color: #1c2030;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  .ew-pay-total-label,
  .ew-pay-total-value {
    padding-top: 10px;
    border-top: 1px solid #eceef5;
    color: #1c2030;
    font-size: 1rem;
    font-weight: 700;
  }
}

@import "../assets/styles/variables";

.ew-tabs {
  border-bottom: 1px solid #e7e7e7;
}

.color-n1 {
  color: #2c2e78;
  font-size: 1.2rem;
}

.color-n2 {
  color: #2c2e78;
}

.tm1 {
  margin-bottom: 7px;
  padding-bottom: 0.7rem;
}

.box-1 {
  border-top: 2px solid #b9b9b9;
}

.background-box1 {
  background: #f7f7f7;
  padding: 5px 20px;
}

.ew-background-whith {
  background: #ffffff;
  padding: 5px 20px;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.16);

}

hr {
  margin-top: 1.5rem;
  margin-bottom: 0rem;
}

.text-black-50 {
  color: #262626 !important;
  margin-bottom: 0.2rem;
}

.font-g1 {
  font-size: 1.2rem;
}

.btn-link {
  font-weight: 400 !important;
}

.field-in1 {
  background: #ededed;
  padding: 0.3rem 0.5rem;
  height: 32px;
}

.pbt1 {
  padding-bottom: 1.2rem;
}

.ew-back-order {
  margin-bottom: 1rem;

}

.ew-back-order {
  a:hover {
    text-decoration: none;
    color: #262626;
  }
}

@media (min-width: 576px) {
  .ew-container-edit {
    margin-left: auto;
    margin-right: auto;
  }
}

// Medium devices (tablets, 768px and up)
@media (min-width: 768px) {
  .ew-container-edit {
    margin-left: auto;
    margin-right: auto;
  }
}

// Large devices (desktops, 992px and up)
@media (min-width: 992px) {
  .ew-container-edit {
    margin-left: 3rem;
    margin-right: 3rem;
  }
}

// Extra large devices (large desktops, 1200px and up)
@media (min-width: 1200px) {
  .ew-container-edit {
    margin-left: 7rem;
    margin-right: 7rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ShipmentDetails.vue